// Layered Canvas 2D renderer.
//
// Three surfaces:
//   baked   - hidden <canvas>, the current page's committed strokes and text, at `bakedView`
//   live    - hidden <canvas>, the active stroke only, drawn incrementally tail by tail
//   visible - the on-screen <canvas>, per frame it is two blits plus a thin overlay pass
//
// The world is never redrawn per frame. A rebake happens only on erase / undo / clear /
// page switch / resize / pan-end. Panning is a translated blit of the baked layer.
// The overlay (marquee, selection box, content being dragged) is drawn straight onto the
// visible canvas after the blits, so selection never dirties the baked layer.

import { getStroke } from 'perfect-freehand';
import { reportBoardFrame } from '../board/performance.ts';
import {
  boundsIntersect,
  boardToScreenX,
  boardToScreenY,
  isEmptyBounds,
  type Bounds,
  type Stroke,
  type TextObject,
  type ViewTransform,
} from './model.ts';
import { textFont, textLines, TEXT_LINE_HEIGHT } from './text.ts';

/** 4K panel on an old GPU - a full-DPR canvas eats too much memory and fill rate. */
const MAX_DPR = 1.5;

/** Points of the previous tail re-drawn with each new tail so the seams do not pinch. */
const TAIL_OVERLAP = 8;

/** Board-space width of an imported page. Height follows from its aspect. */
const ARTBOARD_WIDTH = 1000;
/** Breathing room around the fitted artboard, inside the free board area. */
const FIT_MARGIN = 24;

const INK = {
  thinning: 0.5,
  smoothing: 0.5,
  streamline: 0.45,
  // Touch panels report no real pressure - velocity-based simulation looks better.
  simulatePressure: true,
};

const SELECT_COLOR = '#1d4ed8';
const MARQUEE_FILL = 'rgba(29, 78, 216, 0.08)';
/** Screen-space padding around the selection box so ink is not clipped by the outline. */
const SELECT_PAD_PX = 8;

/** Everything the overlay pass needs. Board space unless noted. */
export interface RendererOverlay {
  marquee: Bounds | null;
  selectionBox: Bounds | null;
  movingStrokes: Stroke[] | null;
  movingTexts: TextObject[] | null;
  dx: number;
  dy: number;
  hidden: { [id: string]: true } | null;
}

export interface RendererDeps {
  getStrokes: () => Stroke[];
  getTexts: () => TextObject[];
  getView: () => ViewTransform;
  getActiveStroke: () => Stroke | null;
  /** Document revision, bumped on every committed mutation and on page switch. */
  getRevision: () => number;
  getOverlay: () => RendererOverlay;
  setView: (view: ViewTransform) => void;
}

export function getDevicePixelRatio(): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  return Math.min(dpr && dpr > 0 ? dpr : 1, MAX_DPR);
}

/** Element resize notifications without depending on ResizeObserver (absent on Chrome 58). */
export function observeElementSize(el: HTMLElement, cb: () => void): () => void {
  const Ctor = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
  if (typeof Ctor === 'function') {
    const ro = new Ctor(cb);
    ro.observe(el);
    return () => ro.disconnect();
  }
  window.addEventListener('resize', cb, false);
  window.addEventListener('orientationchange', cb, false);
  return () => {
    window.removeEventListener('resize', cb, false);
    window.removeEventListener('orientationchange', cb, false);
  };
}

function createLayer(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  return ctx;
}

export class WhiteboardRenderer {
  private readonly visible: HTMLCanvasElement;
  private readonly visibleCtx: CanvasRenderingContext2D;
  private baked: HTMLCanvasElement;
  private bakedCtx: CanvasRenderingContext2D;
  private live: HTMLCanvasElement;
  private liveCtx: CanvasRenderingContext2D;
  private readonly deps: RendererDeps;
  private background: HTMLCanvasElement | null = null;
  private backgroundAspect: number | null = null;
  /** Free board area the artboard is fitted into - the shell publishes it, see setInsets. */
  private insets = { left: 0, right: 0, top: 0, bottom: 0 };

  private dpr = 1;
  private widthDev = 0;
  private heightDev = 0;

  /** View the baked layer was rasterised at - the blit offset comes from the delta. */
  private bakedView: ViewTransform = { x: 0, y: 0, scale: 1 };
  private bakedRevision = -1;
  /** View the live layer is being drawn at - fixed for the duration of a stroke. */
  private liveView: ViewTransform = { x: 0, y: 0, scale: 1 };
  private liveDrawn = 0;

  private frameHandle = 0;
  private disposed = false;

  constructor(visible: HTMLCanvasElement, deps: RendererDeps) {
    this.visible = visible;
    this.visibleCtx = context2d(visible);
    this.deps = deps;
    this.baked = createLayer(1, 1);
    this.bakedCtx = context2d(this.baked);
    this.live = createLayer(1, 1);
    this.liveCtx = context2d(this.live);
  }

  /** A PDF page is a read-only board-space layer underneath all committed and live ink. */
  setBackground(bitmap: HTMLCanvasElement | null, aspect: number | null): void {
    const nextAspect = bitmap && aspect && aspect > 0 ? aspect : null;
    const shouldFit = this.background === null || this.backgroundAspect !== nextAspect;
    this.background = bitmap;
    this.backgroundAspect = nextAspect;
    if (this.backgroundAspect && shouldFit) this.fitBackground();
    else if (!this.backgroundAspect) this.deps.setView({ x: 0, y: 0, scale: 1 });
    this.rebake();
    this.scheduleFrame();
  }

  /** Free board area in CSS pixels. Refits so the artboard never sits under the chrome. */
  setInsets(left: number, right: number, top: number, bottom: number): void {
    const i = this.insets;
    if (left === i.left && right === i.right && top === i.top && bottom === i.bottom) return;
    this.insets = { left, right, top, bottom };
    if (!this.backgroundAspect) return;
    this.fitBackground();
    this.rebake();
    this.scheduleFrame();
  }

  /** Size all layers in device pixels from a CSS-pixel box. Clears them, so rebake after. */
  setSize(cssWidth: number, cssHeight: number): void {
    const dpr = getDevicePixelRatio();
    const w = Math.max(1, Math.round(cssWidth * dpr));
    const h = Math.max(1, Math.round(cssHeight * dpr));
    if (w === this.widthDev && h === this.heightDev && dpr === this.dpr) return;

    this.dpr = dpr;
    this.widthDev = w;
    this.heightDev = h;

    this.visible.width = w;
    this.visible.height = h;
    this.visible.style.width = cssWidth + 'px';
    this.visible.style.height = cssHeight + 'px';

    this.baked.width = w;
    this.baked.height = h;
    this.live.width = w;
    this.live.height = h;

    this.liveDrawn = 0;
    if (this.backgroundAspect) this.fitBackground();
    this.rebake();
    this.scheduleFrame();
  }

  /** Coalesced request for a composite - safe to call per input event. */
  scheduleFrame(): void {
    if (this.frameHandle || this.disposed) return;
    this.frameHandle = window.requestAnimationFrame(this.onFrame);
  }

  /** Start of a stroke: reset the live layer and pin the view it is drawn at. */
  beginActiveStroke(): void {
    const v = this.deps.getView();
    this.liveView = { x: v.x, y: v.y, scale: v.scale };
    this.liveDrawn = 0;
    this.clearLayer(this.liveCtx);
    this.scheduleFrame();
  }

  /** Stroke committed to the document: bake it incrementally, never rebake the world. */
  bakeActiveStroke(stroke: Stroke): void {
    if (this.viewChanged(this.bakedView, this.liveView)) {
      // Baked layer drifted (pan without a rebake) - fall back to a full rebake.
      this.rebake();
    } else {
      this.applyBoardTransform(this.bakedCtx, this.bakedView);
      this.paintStroke(this.bakedCtx, stroke);
      this.bakedRevision = this.deps.getRevision();
    }
    this.liveDrawn = 0;
    this.clearLayer(this.liveCtx);
    this.scheduleFrame();
  }

  cancelActiveStroke(): void {
    this.liveDrawn = 0;
    this.clearLayer(this.liveCtx);
    this.scheduleFrame();
  }

  /** Full redraw of the committed layer at the current view. Call on pan/zoom end. */
  rebake(): void {
    const view = this.deps.getView();
    this.clearLayer(this.bakedCtx);
    this.applyBoardTransform(this.bakedCtx, view);

    const viewport = this.viewportBounds(view);
    const hidden = this.deps.getOverlay().hidden;
    const strokes = this.deps.getStrokes();
    for (let i = 0; i < strokes.length; i++) {
      const s = strokes[i];
      if (hidden && hidden[s.id]) continue;
      if (isEmptyBounds(s.bounds)) continue;
      if (!boundsIntersect(s.bounds, viewport, s.size)) continue;
      this.paintStroke(this.bakedCtx, s);
    }

    const texts = this.deps.getTexts();
    for (let i = 0; i < texts.length; i++) {
      const t = texts[i];
      if (hidden && hidden[t.id]) continue;
      if (!boundsIntersect(t.bounds, viewport, 0)) continue;
      this.paintText(this.bakedCtx, t);
    }

    this.bakedView = { x: view.x, y: view.y, scale: view.scale };
    this.bakedRevision = this.deps.getRevision();
  }

  dispose(): void {
    this.disposed = true;
    if (this.frameHandle) {
      window.cancelAnimationFrame(this.frameHandle);
      this.frameHandle = 0;
    }
    // Release backing stores - old WebViews are stingy with canvas memory.
    this.baked.width = 1;
    this.baked.height = 1;
    this.live.width = 1;
    this.live.height = 1;
  }

  // --- frame ---------------------------------------------------------------

  private onFrame = (): void => {
    this.frameHandle = 0;
    if (this.disposed) return;
    if (this.bakedRevision !== this.deps.getRevision()) this.rebake();
    this.drawLiveTail();
    this.composite();
    reportBoardFrame(Boolean(this.deps.getActiveStroke()));
  };

  /** Draw only the points added since the last frame, plus a short overlap. */
  private drawLiveTail(): void {
    const stroke = this.deps.getActiveStroke();
    if (!stroke) return;
    const total = stroke.points.length;
    if (total === this.liveDrawn) return;

    const start = Math.max(0, this.liveDrawn - TAIL_OVERLAP);
    const tail = stroke.points.slice(start, total);
    this.applyBoardTransform(this.liveCtx, this.liveView);
    this.liveCtx.fillStyle = stroke.color;
    this.fillInk(this.liveCtx, tail, stroke.size, false);
    this.liveDrawn = total;
  }

  private composite(): void {
    const ctx = this.visibleCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.widthDev, this.heightDev);
    const view = this.deps.getView();
    this.drawBackground(ctx, view);
    this.blit(ctx, this.baked, this.bakedView, view);
    if (this.deps.getActiveStroke()) this.blit(ctx, this.live, this.liveView, view);
    this.drawOverlay(ctx, view);
  }

  /**
   * Marquee, selection box and any content lifted for a drag. Bounded by the selection size,
   * never by the document size - a full-world redraw must not happen here.
   */
  private drawOverlay(ctx: CanvasRenderingContext2D, view: ViewTransform): void {
    const o = this.deps.getOverlay();
    const moving = o.movingStrokes || o.movingTexts;
    if (!o.marquee && !o.selectionBox && !moving) return;

    if (moving) {
      this.applyBoardTransform(ctx, view);
      ctx.translate(o.dx, o.dy);
      const ms = o.movingStrokes;
      if (ms) for (let i = 0; i < ms.length; i++) this.paintStroke(ctx, ms[i]);
      const mt = o.movingTexts;
      if (mt) for (let i = 0; i < mt.length; i++) this.paintText(ctx, mt[i]);
    }

    // Boxes are drawn in screen space so their outlines stay 2px at any zoom.
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.strokeStyle = SELECT_COLOR;
    ctx.lineWidth = 2;

    if (o.selectionBox && !isEmptyBounds(o.selectionBox)) {
      const r = this.screenRect(view, o.selectionBox, o.dx, o.dy, SELECT_PAD_PX);
      ctx.setLineDash([]);
      ctx.strokeRect(r[0], r[1], r[2], r[3]);
    }
    if (o.marquee && !isEmptyBounds(o.marquee)) {
      const r = this.screenRect(view, o.marquee, 0, 0, 0);
      ctx.setLineDash([8, 6]);
      ctx.fillStyle = MARQUEE_FILL;
      ctx.fillRect(r[0], r[1], r[2], r[3]);
      ctx.strokeRect(r[0], r[1], r[2], r[3]);
      ctx.setLineDash([]);
    }
  }

  /** Board bounds -> [x, y, w, h] in CSS pixels, offset and inflated. */
  private screenRect(
    view: ViewTransform,
    b: Bounds,
    dx: number,
    dy: number,
    pad: number,
  ): number[] {
    const x0 = boardToScreenX(view, b.minX + dx) - pad;
    const y0 = boardToScreenY(view, b.minY + dy) - pad;
    const x1 = boardToScreenX(view, b.maxX + dx) + pad;
    const y1 = boardToScreenY(view, b.maxY + dy) + pad;
    return [x0, y0, x1 - x0, y1 - y0];
  }

  /** Blit a layer rasterised at `from` as it should appear under `to`. */
  private blit(
    ctx: CanvasRenderingContext2D,
    layer: HTMLCanvasElement,
    from: ViewTransform,
    to: ViewTransform,
  ): void {
    // Layer pixels are already in device space - drawBackground leaves the board transform set.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const k = to.scale / from.scale;
    if (k === 1 && from.x === to.x && from.y === to.y) {
      ctx.drawImage(layer, 0, 0);
      return;
    }
    const tx = (to.x - from.x * k) * this.dpr;
    const ty = (to.y - from.y * k) * this.dpr;
    ctx.drawImage(layer, tx, ty, this.widthDev * k, this.heightDev * k);
  }

  // --- painting ------------------------------------------------------------

  private drawBackground(ctx: CanvasRenderingContext2D, view: ViewTransform): void {
    if (!this.background || !this.backgroundAspect) return;
    // The surround is the shell's dot grid, so paint the artboard only and leave the rest clear.
    const artboardWidth = ARTBOARD_WIDTH;
    const artboardHeight = artboardWidth / this.backgroundAspect;
    this.applyBoardTransform(ctx, view);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, artboardWidth, artboardHeight);
    ctx.drawImage(this.background, 0, 0, artboardWidth, artboardHeight);
  }

  private paintStroke(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
    ctx.fillStyle = stroke.color;
    this.fillInk(ctx, stroke.points, stroke.size, true);
  }

  /** Text is baked with the ink - it is document content, not chrome. */
  private paintText(ctx: CanvasRenderingContext2D, t: TextObject): void {
    if (t.text.length === 0) return;
    ctx.fillStyle = t.color;
    ctx.font = textFont(t.size);
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    const lines = textLines(t.text);
    const lineHeight = t.size * TEXT_LINE_HEIGHT;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].length === 0) continue;
      ctx.fillText(lines[i], t.x, t.y + i * lineHeight);
    }
  }

  private fillInk(
    ctx: CanvasRenderingContext2D,
    points: { x: number; y: number; p: number }[],
    size: number,
    last: boolean,
  ): void {
    if (points.length === 0) return;
    if (points.length === 1) {
      ctx.beginPath();
      ctx.arc(points[0].x, points[0].y, size / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }

    const input: number[][] = new Array(points.length);
    for (let i = 0; i < points.length; i++) {
      const pt = points[i];
      input[i] = [pt.x, pt.y, pt.p];
    }
    const outline = getStroke(input, {
      size,
      thinning: INK.thinning,
      smoothing: INK.smoothing,
      streamline: INK.streamline,
      simulatePressure: INK.simulatePressure,
      last,
    }) as number[][];
    if (outline.length < 3) return;

    ctx.beginPath();
    ctx.moveTo(outline[0][0], outline[0][1]);
    for (let i = 1; i < outline.length; i++) {
      ctx.lineTo(outline[i][0], outline[i][1]);
    }
    ctx.closePath();
    ctx.fill();
  }

  // --- helpers -------------------------------------------------------------

  private applyBoardTransform(ctx: CanvasRenderingContext2D, view: ViewTransform): void {
    const k = this.dpr * view.scale;
    ctx.setTransform(k, 0, 0, k, view.x * this.dpr, view.y * this.dpr);
  }

  private clearLayer(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.widthDev, this.heightDev);
  }

  /** The one zoom-to-fit rule: centre the artboard in the free board area with equal insets. */
  private fitBackground(): void {
    if (!this.backgroundAspect) return;
    const width = this.widthDev / this.dpr;
    const height = this.heightDev / this.dpr;
    const artboardWidth = ARTBOARD_WIDTH;
    const artboardHeight = artboardWidth / this.backgroundAspect;
    const boxWidth = width - this.insets.left - this.insets.right - FIT_MARGIN * 2;
    const boxHeight = height - this.insets.top - this.insets.bottom - FIT_MARGIN * 2;
    const scale = Math.max(0.01, Math.min(boxWidth / artboardWidth, boxHeight / artboardHeight));
    this.deps.setView({
      x: this.insets.left + (width - this.insets.left - this.insets.right - artboardWidth * scale) / 2,
      y: this.insets.top + (height - this.insets.top - this.insets.bottom - artboardHeight * scale) / 2,
      scale,
    });
  }

  /** Visible area expressed in board space, used to cull strokes on rebake. */
  private viewportBounds(view: ViewTransform): Bounds {
    const w = this.widthDev / this.dpr;
    const h = this.heightDev / this.dpr;
    return {
      minX: -view.x / view.scale,
      minY: -view.y / view.scale,
      maxX: (w - view.x) / view.scale,
      maxY: (h - view.y) / view.scale,
    };
  }

  private viewChanged(a: ViewTransform, b: ViewTransform): boolean {
    return a.x !== b.x || a.y !== b.y || a.scale !== b.scale;
  }
}
